import React from "react";
import { Trash2, X, AlertTriangle, AlertCircle, Loader2 } from "lucide-react";

/**
 * Delete Event Modal Component
 * Confirmation modal for permanently deleting an event
 */
const DeleteEventModal = ({
  isOpen,
  onClose,
  onConfirm,
  eventName,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all">
        {/* Header */}
        <div className="flex items-start mb-6 space-x-4">
          <div className="flex-shrink-0 p-3 bg-red-100 rounded-full">
            <Trash2 className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="mb-1 text-xl font-bold text-gray-900">
              Delete Event
            </h3>
            <p className="text-sm text-gray-600">
              Are you sure you want to permanently delete{" "}
              <span className="font-semibold text-gray-900">
                {eventName || "this event"}
              </span>
              ?
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning Message */}
        <div className="p-4 mb-6 bg-red-50 rounded-xl border border-red-100">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="mb-2 font-medium">This action cannot be undone!</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>All event data will be permanently deleted</li>
                <li>All ticket sales and attendee information will be lost</li>
                <li>This event will be removed from all listings</li>
                <li>No refunds can be processed after deletion</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Additional Warning */}
        <div className="p-4 mb-6 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p>
                If you want to temporarily hide this event, consider{" "}
                <span className="font-semibold">marking it as closed</span>{" "}
                instead of deleting it.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white bg-red-600 rounded-xl transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Permanently"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteEventModal;

